// ad.en.ts
const ad = {
  start: {
    title: "Advertising",
    subtitle:
      "Promote your posts as commercial videos, track billing, and see how your campaigns perform.",

    buttons: {
      newCampaign: "Create new campaign",
    },

    billingTitle: "Billing overview",

    billing: {
      managePayment: "Manage Billings",
      spendThisMonth: "Spend this month",
      spendHint: "Based on all running campaigns in the current billing period.",
      impressions: "Impressions",
      impressionsHint: "Total times your ads were shown.",
      clicks: "Clicks",
      clicksHint: "Visits to your links from ads.",
      paymentMethod: "Payment method",
      paymentHint: "",
      changePayment: "Change Payment method",
      manageBillings: 'Manage Billings',
    },

    status: {
      active: "Ad account active",
    },

    steps: {
      title: "How advertising works",
      step1: "Choose an existing post to promote or upload a new commercial video.",
      step2: "Set your daily budget and target countries/cities where you want to show ads.",
      step3:
        "Add a link and call-to-action so people can visit your website or profile.",
      step4: "Review performance here and pause or edit campaigns any time.",
    },

    campaigns: {
      title: "Your campaigns",
      none: "No campaigns yet",
      count: "{{count}} active / draft",
      emptyHint:
        "Once you create your first advertising post, it will appear here with billing and performance details.",
      columns: {
        name: "Campaign",
        status: "Status",
        spend: "Spend (this month)",
        impressions: "Impressions",
        clicks: "Clicks",
      },
      pause: "Pause",
      resume: "Resume",
      end: "End",
      view: "View",
    },
  },

  overview: {
    alerts: {
      noMediaAttached: "No media attached to this ad.",
      missingTargetUrl: "Missing target URL.",
      invalidUrl: "Please enter a valid URL starting with http:// or https://",
      uploadNoMediaProcessed: "Upload failed: no media could be processed.",
      launchFailed: "Failed to launch campaign. Please try again.",
    },

    campaignDetails: {
      title: "Campaign details",
      nameLabel: "Campaign / project name",
      namePlaceholder: "e.g. Spring sale video",
      helper:
        "This name will appear in your advertising overview but not in the advertisement.",
    },

    targeting: {
      title: "Where should your ad appear?",
      modes: {
        world: "🌍 Worldwide",
        continent: "🌐 By continent",
        countries: "🗺️ By country",
      },
      continentLabel: "Continent",
      continentHelperPrefix: "Your ad will be eligible to show to users across",
      countriesLabel: "Countries",
      selectedPrefix: "Selected:",
      noneSelected: "No country selected yet",
    },

    budget: {
      title: "Budget & delivery",
      dailyBudgetLabel: "Daily budget",
      perDaySuffix: "€ / day",
      endDateLabel:
        "End date (optional) Leave empty for unlimited duration. It is possible to end the advertisement at anytime in the ad manager.",
      deliveryQuestion: "How often should we push your ad?",
      deliveryModes: {
        light: "💤 Light",
        balanced: "⚖️ Balanced",
        frequent: "🚀 Frequent",
      },
      estimatePrefix: "With",
      estimateMiddle: "your ad can reach approximately",
      estimateSuffix: "impressions per day.",
    },

    actions: {
      back: "← Back",
      launch: "🚀 Launch campaign & upload",
    },

    summary: {
      title: "Campaign summary",
      rows: {
        location: "Location",
        dailyBudget: "Daily budget",
        totalBudget: "Total budget",
        endDate: "End date",
        delivery: "Delivery",
      },
      worldwide: "Worldwide",
      noCountrySelected: "No country selected",
      deliveryValues: {
        light: "Light (show less often)",
        balanced: "Balanced",
        frequent: "Frequent (show more often)",
      },
      billingNote: "Billed per 1000 impressions (CPM) in EUR.",
    },

    preview: {
      sponsored: "Sponsored",
      visitWebsiteFallback: "Visit website",
      ctaDisabledTitle:
        "Add a valid URL on the previous step to enable this button",
      noMediaAttachedHint:
        "No media attached – go back and add at least one image or video.",
    },
  },

  upload: {
    header: {
      title: "Create a new ad",
    },
    media: {
      uploading: "Uploading",
      noPreview: "No preview available",
      addTile: {
        title: "Click here to choose Images & Videos",
        subtitle: "…or drop them here",
      },
    },
    buttons: {
      filter: "Filter",
      music: "Music",
      delete: "Delete",
    },
  },

  new: {
    alerts: {
      needMedia: "Please add at least one image or video.",
      needUrl: "Please enter a link for your ad.",
      badUrl: "Please enter a valid URL starting with http:// or https://",
      needButtonLabel:
        'Please enter a label for your ad button (e.g. "Shop now").',
    },
  },

  alerts: {
    mediaPerPostLimit:
      "You reached the maximum number of media items for this ad.",
    mediaPerPostHardLimit:
      "Some files were skipped because the media limit was reached.",
    videosSkippedTooLongShort:
      "Some videos were skipped because they are too long.",
    videosSkippedTooLongSummary:
      "Some videos were skipped because they exceed the maximum duration.",
    filesSkippedByCapSummary:
      "Some files were skipped because the media limit was reached.",
  },

  // ✅ ADD THIS BLOCK (for ChangePayment.tsx)
  payment: {
    title: "Change payment method",
    subtitle:
      "Select how you want to pay for your advertising campaigns. You can update this at any time.",

    backToBilling: "Back to Advertisement",

    saved: "Your payment method has been updated (demo only, no real billing yet).",

    buttons: {
      save: "Save changes",
      cancel: "Cancel",
    },

    methods: {
      title: "Payment methods",
      card: "Credit / Debit card",
      sepa: "SEPA bank debit",
      wallet: "Apple / Google Pay",
      note:
        "Your default method will be used for all future charges unless a campaign requires additional confirmation.",
    },

    card: {
      name: "Card holder name",
      namePlaceholder: "Name as displayed on the card",
      number: "Card number",
      expiry: "Expiry",
      cvc: "CVC",
      hint:
        "We support Visa, Mastercard and American Express. Your card details are processed securely.",
    },

    paypal: {
      email: "PayPal email",
      hint:
        "You will be redirected to PayPal for confirming future charges when needed.",
    },

    sepa: {
      name: "Account holder",
      namePlaceholder: "Full name of the account owner",
      iban: "IBAN",
      hint:
        "By saving a SEPA bank account you authorise recurring debits for your advertising charges.",
    },

    wallet: {
      provider: "Wallet provider",
      apple: "Apple Pay",
      google: "Google Pay",
      hint:
        "You will complete the connection with your wallet provider on your next payment confirmation.",
    },

    security: {
      title: "Security & billing info",
      point1:
        "Card and bank details are sent via encrypted connections and are never stored directly on our servers.",
      point2:
        "Charges are usually bundled once per day for active campaigns, based on impressions and clicks.",
      point3:
        "You can pause campaigns at any time from the Ads overview to stop future charges.",
    },

    currentMethod: {
      label: "Current method (example)",
      hint:
        "New charges will use the method you save here. This block only shows a demo card for now.",
    },

    smallPrint:
      "Important: This is a preview UI. In production, please ensure you comply with local regulations (PSD2, SCA, card scheme rules, etc.) and use a PCI-compliant payment provider.",
  },


  // ✅ ADD THIS BLOCK inside your existing `ad` object (en.ad.ts)

billing: {
  title: "Manage billings",
  subtitle:
    "Set spending limits, review monthly costs, and see how much each campaign consumes.",

  backToOverview: "Back to Ads overview",
  loading: "Loading billing data...",
  allTime: "All time",

  // Monthly limit card
  limitTitle: "Monthly spending limit",
  limitDescription:
    "Set a soft limit for ad spend this month. We’ll warn you when your campaigns approach this amount.",
  limitLabel: "Limit for",
  thisPeriod: "this period",
  saveLimits: "Save",
  currentSpend: "Current spend",

  hints: {
    safe: "Your spend is within a safe range for this month.",
    approaching:
      "You are approaching your monthly limit, consider lowering budgets or pausing some campaigns.",
    exceeded:
      "You have exceeded your monthly limit. Future campaigns may continue unless you pause them.",
  },

  // Overview card
  lastMonthsTitle: "Last months overview",
  lifetimeSpend: "Lifetime spend",
  lifetimeImpressions: "Total impressions",
  lifetimeClicks: "Total clicks",

  // Monthly stats card
  monthlyStatsTitle: "Monthly statistics",
  table: {
    spend: "Spend",
    impressions: "Impressions",
    clicks: "Clicks",
    cpm: "Average CPM",
    cpmHint: "per 1000 impressions",
    cpc: "Average CPC",
    cpcHint: "per click",
  },

  // Campaign breakdown card
  campaignBreakdownTitle: "Campaign breakdown for {{month}}",
  campaignBreakdownEmpty: "No campaigns had spend in this month.",
  campaignColumns: {
    name: "Campaign",
    spend: "Spend",
    impressions: "Impr.",
    clicks: "Clicks",
    view: "View",
  },
},


viewDetail: {
  loading: "Loading campaign…",

  errors: {
    loadFailed: "Failed to load advertisement",
    notFound: "Campaign not found.",
  },

  buttons: {
    backToOverview: "Back to overview",
    back: "Back",
    pause: "Pause",
    resume: "Resume",
    endCampaign: "End campaign",
  },

  confirm: {
    endCampaign: "End this campaign? It will stop delivering.",
  },

  alerts: {
    updateStatusFailed: "Failed to update campaign status. Please try again.",
  },

  header: {
    campaignId: "Campaign ID: {{id}}",
  },

  sections: {
    performance: "Performance",
    budgetDelivery: "Budget & delivery",
    targeting: "Targeting",
    destination: "Destination",
    adPreview: "Ad preview",
  },

  performance: {
    impressions: "Impressions",
    clicks: "Clicks",
    ctr: "CTR",
    spendTotal: "Spend total",
    cpmEffective: "CPM (effective)",
    cpmConfigured: "CPM (configured)",
  },

  budget: {
    dailyBudget: "Daily budget",
    totalBudget: "Total budget",
    unlimited: "Unlimited",
    delivery: "Delivery",
    endDate: "End date",
    deliveryValues: {
      light: "Light (show less often)",
      balanced: "Balanced",
      frequent: "Frequent (show more often)",
    },
  },

  targeting: {
    worldwide: "Worldwide",
    continents: "Continents: {{list}}",
    countries: "Countries: {{list}}",
    cities: "Cities: {{list}}",
    none: "No specific targeting",
    hashtags: "Hashtags: {{list}}",
  },

  destination: {
    urlLabel: "URL",
    buttonLabel: "Button label",
    dash: "—",
  },

  preview: {
    adCampaignFallback: "Ad campaign",
    sponsoredFallback: "Sponsored",
    visitWebsiteFallback: "Visit website",
    noMediaAttached: "No media attached to this ad.",
  },
},


sidebar: {
  heading: 'Advertisement setup',
  description:
    'Choose on which website the user should land when they tap your ad button.',

  urlLabel: 'Destination URL',
  urlPlaceholder: 'Please paste your URL here, e.g. https://your-shop.com',
  urlHint:
    'Use a short name for the button that draws interest for your product/service. It is also recommended to use the company name like Visit Elnydo',

  buttonLabel: 'Button text',
  buttonPlaceholder: 'e.g. Shop now, Learn more',

  buttons: {
    continue: 'Continue',
    back: 'Back',
  },
},

} as const;

export default ad;
